import { Link, useParams } from 'react-router-dom';
import SiteShell from '@/components/feature/SiteShell';
import Breadcrumb from '@/components/base/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsArticles } from '@/mocks/news';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const article = newsArticles.find((item) => item.id === id);
  const index = article ? newsArticles.findIndex((item) => item.id === article.id) : -1;
  const related = article
    ? newsArticles.filter((item) => item.category === article.category && item.id !== article.id).slice(0, 3)
    : [];
  const prev = index > 0 ? newsArticles[index - 1] : undefined;
  const next = index >= 0 && index < newsArticles.length - 1 ? newsArticles[index + 1] : undefined;

  usePageMeta({
    title: article ? `${article.title} - 影视资讯 - 黄牛视频` : '影视资讯 - 黄牛视频',
    description: article ? article.summary : '黄牛视频影视资讯详情页。',
    keywords: article ? `${article.category},影视资讯,${article.title},黄牛视频` : '影视资讯,黄牛视频',
  });

  if (!article) {
    return (
      <SiteShell>
        <div className="mx-auto w-full max-w-3xl px-4 py-24 text-center md:px-8">
          <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这篇资讯</h1>
          <p className="mt-3 text-sm text-foreground-500">
            文章可能已被删除或链接有误，返回资讯频道可以查看全部报道。
          </p>
          <Link
            to="/news"
            className="mt-6 inline-flex rounded-md bg-primary-900 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-800"
          >
            返回资讯频道
          </Link>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div className="w-full px-4 pb-2 pt-5 md:px-8 md:pt-7">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '影视资讯', to: '/news' },
            { label: article.title },
          ]}
        />
      </div>

      <div className="w-full px-4 pb-14 pt-4 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-10">
          <article className="min-w-0">
            <header>
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="rounded-sm bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                  {article.category}
                </span>
                <span className="text-foreground-400">{article.publishedAt}</span>
                <span className="text-foreground-300">·</span>
                <span className="text-foreground-400">来源：{article.source}</span>
              </div>
              <h1 className="mt-3 font-heading text-2xl font-extrabold leading-snug tracking-tight text-foreground-950 md:text-3xl lg:text-[34px]">
                {article.title}
              </h1>
              <p className="mt-4 rounded-lg border-l-2 border-accent-500 bg-background-100/70 px-4 py-3 text-sm leading-relaxed text-foreground-600">
                {article.summary}
              </p>
            </header>

            <div className="mt-6 w-full overflow-hidden rounded-lg bg-background-200">
              <div className="aspect-[16/9] w-full">
                <img
                  src={article.cover}
                  alt={`${article.title} 配图`}
                  title={`${article.title} - 影视资讯 - 黄牛视频`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="mt-6 space-y-4">
              {article.content.map((paragraph, paragraphIndex) => (
                <p
                  key={`${article.id}-p${paragraphIndex}`}
                  className="text-sm leading-8 text-foreground-700 md:text-[15px]"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
              <span className="text-[11px] text-foreground-400">相关标签</span>
              {[article.category, '影视资讯', '黄牛视频', '幕后'].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-100 px-2.5 py-1 text-[11px] text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>

            <p className="mt-4 text-[11px] leading-relaxed text-foreground-400">
              本文为示例资讯内容，人物、作品与数据均为虚构，仅用于展示影视资讯页面的结构与阅读体验。
            </p>

            <nav className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {prev ? (
                <Link
                  to={`/news/${prev.id}`}
                  className="group rounded-lg border border-background-200 p-3.5 transition-colors hover:border-primary-200 hover:bg-primary-50/40"
                >
                  <span className="flex items-center gap-1 text-[11px] text-foreground-400">
                    <i className="ri-arrow-left-line" /> 上一篇
                  </span>
                  <p className="mt-1.5 line-clamp-2 text-xs font-medium text-foreground-800 group-hover:text-primary-700">
                    {prev.title}
                  </p>
                </Link>
              ) : (
                <div className="rounded-lg border border-dashed border-background-200 p-3.5 text-xs text-foreground-400">
                  已经是第一篇资讯
                </div>
              )}
              {next ? (
                <Link
                  to={`/news/${next.id}`}
                  className="group rounded-lg border border-background-200 p-3.5 text-right transition-colors hover:border-primary-200 hover:bg-primary-50/40"
                >
                  <span className="flex items-center justify-end gap-1 text-[11px] text-foreground-400">
                    下一篇 <i className="ri-arrow-right-line" />
                  </span>
                  <p className="mt-1.5 line-clamp-2 text-xs font-medium text-foreground-800 group-hover:text-primary-700">
                    {next.title}
                  </p>
                </Link>
              ) : (
                <div className="rounded-lg border border-dashed border-background-200 p-3.5 text-right text-xs text-foreground-400">
                  已经是最后一篇资讯
                </div>
              )}
            </nav>
          </article>

          <aside className="space-y-4 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="font-heading text-sm font-bold text-foreground-950">同类资讯</h2>
              <ul className="mt-3 space-y-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex gap-2.5">
                      <div className="h-12 w-16 shrink-0 overflow-hidden rounded bg-background-200">
                        <img
                          src={item.cover}
                          alt={`${item.title} 配图`}
                          title={`${item.title} - 相关资讯 - 黄牛视频`}
                          className="h-full w-full object-cover object-top"
                        />
                      </div>
                      <p className="line-clamp-2 text-xs leading-relaxed text-foreground-700 group-hover:text-primary-700">
                        {item.title}
                      </p>
                    </Link>
                  </li>
                ))}
                {related.length === 0 && (
                  <li className="text-xs text-foreground-400">暂无同类资讯</li>
                )}
              </ul>
              <Link
                to="/news"
                className="mt-4 flex items-center justify-between rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-700 transition-colors hover:bg-background-200"
              >
                返回资讯频道
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            <div className="rounded-lg bg-primary-900 p-4">
              <p className="font-heading text-sm font-bold text-background-50">相关影视作品</p>
              <p className="mt-2 text-[11px] leading-relaxed text-primary-200">
                在分类频道里查看本季度新片与热门剧集，包含简介、主创与评分信息。
              </p>
              <Link
                to="/category/movie"
                className="mt-3 flex items-center justify-between rounded-md bg-background-50/10 px-3 py-2 text-xs text-background-50 transition-colors hover:bg-background-50/20"
              >
                浏览电影频道
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </SiteShell>
  );
}